import {
    EnvironmentProviders,
    inject,
    provideAppInitializer
} from '@angular/core';
import { PRIMARY_OUTLET, UrlSerializer } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ClientService } from './services/client.service';

/** Where a visitor is sent when the URL carries no client code. */
export const NO_CLIENT_REDIRECT_URL =
    'https://geniuslearning.com/laces-solutions/';

/**
 * Pulls the client code out of the URL path.  The path differs per environment:
 *   Production: https://studentportal.literacypro.com/wy/home
 *   QA:         https://test.literacypro.com/agletqa/wy/home
 *   Local:      https://localhost:4201/wy/home
 * For local testing of different paths, set <base href="/agletlocal/"/> in index.html then
 * run "ng serve --serve-path agletlocal".
 */
export function resolveClientCode(
    urlSerializer: UrlSerializer,
    pathname: string
): string | undefined {
    const segments =
        urlSerializer.parse(pathname).root.children[PRIMARY_OUTLET]?.segments;

    if (segments?.length >= 2 && segments[0].path.startsWith('aglet')) {
        return segments[1].path;
    }
    if (segments?.length >= 1) {
        return segments[0].path;
    }
    return undefined;
}

/**
 * Resolves the client code and loads the client BEFORE the application bootstraps.
 *
 * `runtimeconfig.js` seeds `clientId: 0`, and the real id is only assigned when
 * GET /clients/{clientCode} returns.  Seven services read `runtimeConfig.clientId`
 * straight off the global at call time — including DynamicFormApiService, which builds its
 * URL in a component field initializer.  Without this gate, any cold load that lands
 * directly on a form (most commonly the window.location.reload() a language switch
 * performs) can request /clients/0/dynamic-form and render a form with no controls.
 *
 * Failure is deliberately non-fatal: ClientService.load$ catches errors, so a client fetch
 * that fails cannot leave the user staring at a blank page forever.  AppComponent still
 * calls ClientService.load(), which retries when nothing was published here.
 */
export function provideClientBootstrap(): EnvironmentProviders {
    return provideAppInitializer(() => {
        const urlSerializer = inject(UrlSerializer);
        const clientService = inject(ClientService);

        const clientCode = resolveClientCode(
            urlSerializer,
            window.location.pathname
        );

        if (!clientCode) {
            // No client code in the URL — send the visitor to the LACES marketing site.
            window.location.href = NO_CLIENT_REDIRECT_URL;
            return Promise.resolve();
        }

        runtimeConfig.clientCode = clientCode;

        return firstValueFrom(clientService.load$());
    });
}
